import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  taskCategoryListSchema,
  type TaskCategory,
  type TaskCategoryList,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';

/**
 * What this shop calls the things it undertakes to do about somebody — "Call Back", "Send Quote".
 *
 * Its own query key rather than a slice of a customer's: the list changes when the shop edits its
 * settings, not when somebody logs a call, and invalidating a customer should not re-fetch it.
 *
 * Read-only here. The API supports the full add / rename / retire / reorder set, so a settings
 * screen can be built against it whenever a shop asks for one; until then every shop runs on the
 * seeded defaults, which is a working product rather than an empty picker.
 */

const PATH = '/settings/task-categories';

export async function getTaskCategories(client: ApiClient): Promise<TaskCategoryList> {
  return await client.get(PATH, { schema: taskCategoryListSchema });
}

export const taskCategoryKeys = {
  all: ['task-categories'] as const,
  list: () => ['task-categories', 'list'] as const,
};

export function useTaskCategories(): UseQueryResult<TaskCategoryList> {
  const client = useApiClient();
  return useQuery({
    queryKey: taskCategoryKeys.list(),
    queryFn: () => getTaskCategories(client),
  });
}

/** The ones a picker may offer. A retired category still resolves a label, and is never offered. */
export function offerableTaskCategories(
  categories: readonly TaskCategory[] | undefined,
): TaskCategory[] {
  return (categories ?? []).filter((category) => category.active);
}
